import type { MutableRefObject } from 'react';
import { AlertCircle, Sun, Sunrise, Calendar, Clock, CheckCircle } from 'lucide-react';
import type { TodoWithSource, TodoGroup as TodoGroupType } from '../../types/todo';
import { TODO_GROUP_LABELS } from '../../types/todo';
import { ToDoItem } from './ToDoItem';

interface ToDoGroupProps {
  group: TodoGroupType;
  todos: TodoWithSource[];
  onToggle: (id: string, completed: boolean) => void;
  onDelete: (id: string) => void;
  onEdit: (todo: TodoWithSource) => void;
  focusedTodoId?: string | null;
  todoRefs?: MutableRefObject<Map<string, HTMLDivElement>>;
}

const groupIcons: Record<TodoGroupType, typeof Sun> = {
  OVERDUE: AlertCircle,
  TODAY: Sun,
  TOMORROW: Sunrise,
  THIS_WEEK: Calendar,
  LATER: Clock,
  COMPLETED: CheckCircle,
};

const groupColors: Record<TodoGroupType, { icon: string; bg: string; text: string }> = {
  OVERDUE: { icon: 'text-red-500', bg: 'bg-red-50', text: 'text-red-700' },
  TODAY: { icon: 'text-amber-500', bg: 'bg-amber-50', text: 'text-amber-700' },
  TOMORROW: { icon: 'text-cyan-500', bg: 'bg-cyan-50', text: 'text-cyan-700' },
  THIS_WEEK: { icon: 'text-blue-500', bg: 'bg-blue-50', text: 'text-blue-700' },
  LATER: { icon: 'text-gray-500', bg: 'bg-gray-50', text: 'text-gray-700' },
  COMPLETED: { icon: 'text-green-500', bg: 'bg-green-50', text: 'text-green-700' },
};

export function ToDoGroup({ group, todos, onToggle, onDelete, onEdit, focusedTodoId, todoRefs }: ToDoGroupProps) {
  const Icon = groupIcons[group];
  const colors = groupColors[group];
  const label = TODO_GROUP_LABELS[group];

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <div className={`w-8 h-8 rounded-lg ${colors.bg} flex items-center justify-center`}>
          <Icon className={`w-4 h-4 ${colors.icon}`} />
        </div>
        <h3 className={`font-semibold ${colors.text}`}>{label}</h3>
        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${colors.bg} ${colors.text}`}>
          {todos.length}
        </span>
      </div>

      <div className="space-y-2 pl-11">
        {todos.map((todo) => (
          <ToDoItem
            key={todo.id}
            ref={(el) => {
              if (el && todoRefs) {
                todoRefs.current.set(todo.id, el);
              }
            }}
            todo={todo}
            onToggle={onToggle}
            onDelete={onDelete}
            onEdit={onEdit}
            isFocused={focusedTodoId === todo.id}
          />
        ))}
      </div>
    </div>
  );
}
